/**
 * Integrations Redesign — Types & Interfaces
 *
 * New types introduced for the Settings → Integrations redesign (INT-01).
 * These types model the simplified integration setup (product + externalInstanceId)
 * and the user-to-company access management feature.
 *
 * Re-uses CompanyStatus and IntegrationConnectionStatus from ./company where possible.
 */

import type { CompanyStatus, IntegrationConnectionStatus } from './company';

// ---------------------------------------------------------------------------
// Integration Product Options
// ---------------------------------------------------------------------------

/** Supported financial integration products */
export type FinancialProduct =
  | 'access-financials'
  | 'access-dimensions'
  | 'xero'
  | 'sage'
  | 'quickbooks'
  | 'other';

/** Supported HR integration products */
export type HRProduct =
  | 'people-hr'
  | 'bamboohr'
  | 'adp'
  | 'paycircle'
  | 'workday'
  | 'other';

/** Supported project integration products */
export type ProjectProduct =
  | 'access-projects'
  | 'access-focalpoint'
  | 'access-financials-operations'
  | 'other';

/** Supported planner integration products */
export type PlannerProduct =
  | 'access-resource-scheduler'
  | 'access-planner'
  | 'people-hr'
  | 'other';

/** Human-readable label for a financial product */
export const FINANCIAL_PRODUCT_LABELS: Record<FinancialProduct, string> = {
  'access-financials': 'Access Financials',
  'access-dimensions': 'Access Dimensions',
  xero: 'Xero',
  sage: 'Sage',
  quickbooks: 'QuickBooks',
  other: 'Other',
};

/** Human-readable label for an HR product */
export const HR_PRODUCT_LABELS: Record<HRProduct, string> = {
  'people-hr': 'People HR',
  bamboohr: 'BambooHR',
  adp: 'ADP',
  paycircle: 'Paycircle',
  workday: 'Workday',
  other: 'Other',
};

/** Human-readable label for a project product */
export const PROJECT_PRODUCT_LABELS: Record<ProjectProduct, string> = {
  'access-projects': 'Access Projects',
  'access-focalpoint': 'Access FocalPoint',
  'access-financials-operations': 'Access Financial Operations',
  other: 'Other',
};

/** Human-readable label for a planner product */
export const PLANNER_PRODUCT_LABELS: Record<PlannerProduct, string> = {
  'access-resource-scheduler': 'Resource Scheduler',
  'access-planner': 'Access Planner',
  'people-hr': 'People HR',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Integration Config (simplified — product + externalInstanceId only)
// ---------------------------------------------------------------------------

/**
 * Simplified integration configuration per the redesign spec.
 * Replaces the legacy IntegrationConfig for the new sheet UI.
 */
export interface SimpleIntegrationConfig {
  /** Which product is connected (null = not configured) */
  product: FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | null;
  /** The external workspace instance ID */
  externalInstanceId: string | null;
  /** Secondary external instance ID (e.g. EBProjects CompanyId for access-projects) */
  externalInstanceIdSecondary?: string | null;
  /** Human-readable name of the connected product instance */
  instanceName?: string | null;
  /** Current connection status */
  status: IntegrationConnectionStatus;
  /** ISO timestamp of the last successful sync */
  lastSyncAt: string | null;
  /** ISO timestamp of the last full re-sync */
  lastFullSyncAt: string | null;
  /** Default project ID for absence entries (HR integration only) */
  defaultProjectId?: string | null;
  /** Default cost centre ID for absence entries (HR integration only) */
  defaultCostCentreId?: string | null;
}

// ---------------------------------------------------------------------------
// Company with Integrations (enriched shape returned by GET /companies/{id})
// ---------------------------------------------------------------------------

/**
 * Enriched company shape that includes integration details and user count.
 * This is what the sheet and list consume.
 */
export interface CompanyWithIntegrations {
  id: string;
  name: string;
  code: string;
  status: CompanyStatus;
  financialIntegration: SimpleIntegrationConfig;
  hrIntegration: SimpleIntegrationConfig;
  projectIntegration: SimpleIntegrationConfig;
  plannerIntegration: SimpleIntegrationConfig;
  /** Total number of users assigned to this company */
  userCount: number;
}

// ---------------------------------------------------------------------------
// Company User Access
// ---------------------------------------------------------------------------

/**
 * A single user's access record for a company.
 * Maps to the backend CompanyUserAccess model.
 */
export interface CompanyUserAccess {
  userId: string;
  companyId: string;
  isDefault: boolean;
}

/**
 * A user displayed in the "Users with Access" section of the company sheet.
 */
export interface CompanyUser {
  id: string;
  name: string;
  email: string;
  role: string;
  /** URL to the user's avatar image (may be undefined) */
  photoUrl?: string;
  /** Whether this company is the user's default */
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Employee Company Access (for Team Members → Company Access tab)
// ---------------------------------------------------------------------------

/**
 * A company row as displayed in the Team Member Company Access tab.
 * Combines company info with the employee's current access state.
 */
export interface EmployeeCompanyAccessRow {
  companyId: string;
  companyName: string;
  companyCode: string;
  companyStatus: CompanyStatus;
  /** Whether the employee currently has access to this company */
  hasAccess: boolean;
  /** Whether this company is set as the employee's default */
  isDefault: boolean;
}

/**
 * The payload for PATCH /employees/{id}/company-access.
 */
export interface UpdateEmployeeCompanyAccessPayload {
  companyAccess: Array<{
    companyId: string;
    isDefault: boolean;
  }>;
}

// ---------------------------------------------------------------------------
// API Request / Response Payloads
// ---------------------------------------------------------------------------

/**
 * Payload for PUT /companies/{id}/integrations
 */
export interface UpdateIntegrationsPayload {
  financial: {
    product: FinancialProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
  };
  hr: {
    product: HRProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
    defaultProjectId?: string | null;
    defaultCostCentreId?: string | null;
  };
  project: {
    product: ProjectProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
    externalInstanceIdSecondary?: string | null;
  };
  planner: {
    product: PlannerProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
    externalInstanceIdSecondary?: string | null;
  };
}

/**
 * Payload for POST /companies/{id}/integrations/test
 */
export interface TestIntegrationPayload {
  type: 'financial' | 'hr' | 'project' | 'planner';
}

/**
 * Response from POST /companies/{id}/integrations/test
 */
export interface TestIntegrationResponse {
  success: boolean;
  message: string;
  testedAt: string;
  /** The Version returned by the Financial Operations connectionTest query (if available) */
  tsoVersion?: string;
}

/**
 * Payload for POST /companies/{id}/users
 */
export interface AddUserToCompanyPayload {
  userId: string;
}
